import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { type AiInsight, aiInsightsApi, type Lens } from '@/features/ai-insights'

/** Status buckets the history loader can fetch. `active` is served live by
 *  `useAiInsights`, so this hook only loads the terminal history buckets. */
export type InsightHistoryStatus = 'accepted' | 'skipped'

interface UseInsightHistoryResult {
  history: AiInsight[]
  loading: boolean
}

/** Loads accepted/skipped insight history for the full-page AI insights view.
 *
 *  Only fetches when `enabled` (i.e. a history tab — not the live `active`
 *  tab — is selected), and cancels in-flight requests when the lens, status,
 *  or case changes to avoid races writing stale rows. */
export function useInsightHistory(
  caseId: string | null,
  lens: Lens,
  status: InsightHistoryStatus,
  enabled: boolean
): UseInsightHistoryResult {
  const [history, setHistory] = useState<AiInsight[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!caseId || !enabled) return
    let cancelled = false
    setLoading(true)
    aiInsightsApi
      .list(caseId, lens, status)
      .then(rows => {
        if (!cancelled) setHistory(rows)
      })
      .catch(err => {
        if (!cancelled) toast.error(`Failed to load ${status} insights: ${(err as Error).message}`)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [caseId, lens, status, enabled])

  return { history, loading }
}
